<script setup>
import { nextTick, onBeforeUnmount, ref } from 'vue';
import EmojiPickerButton from './EmojiPickerButton.vue';
import { QUICK_REACTIONS } from '../../helpers/messageEmoji';

const props = defineProps({
  reactions: { type: Array, default: () => [] },
  mine: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle']);

const trigger = ref(null);
const bar = ref(null);
const place = ref(null);

// The full picker is drawn on the body too, and choosing from it must not count as leaving the bar.
const inside = (target) => bar.value?.contains(target) || trigger.value?.contains(target) || target.closest?.('.pge-tooltip');

const backToTrigger = () => trigger.value?.focus();

const outside = (event) => {
  if (inside(event.target)) return;
  close();
  // Once the click has settled focus: if it landed nowhere, it comes back to this message.
  setTimeout(() => {
    if (document.activeElement === document.body) backToTrigger();
  });
};

const escape = (event) => {
  if (event.key !== 'Escape') return;
  close();
  backToTrigger();
};

function close() {
  place.value = null;
  document.removeEventListener('mousedown', outside);
  document.removeEventListener('keydown', escape);
}

// Drawn on the body: the portal's message pane scrolls and would clip it.
const open = async () => {
  if (place.value) return close();

  const r = trigger.value.getBoundingClientRect();
  place.value = { left: `${r.left}px`, top: `${r.top}px` };
  await nextTick();

  const w = bar.value.offsetWidth;
  const h = bar.value.offsetHeight;
  const top = r.top - h - 8 < 10 ? Math.min(r.bottom + 8, window.innerHeight - h - 10) : r.top - h - 8;
  place.value = { left: `${Math.min(Math.max(10, r.left - 6), window.innerWidth - w - 10)}px`, top: `${top}px` };
  document.addEventListener('mousedown', outside);
  document.addEventListener('keydown', escape);
};

const pick = (emoji) => {
  close();
  backToTrigger();
  emit('toggle', emoji);
};

onBeforeUnmount(close);
</script>

<template>
  <div class="rxrow" :class="{ me: props.mine, has: props.reactions.length }" data-testid="reactions">
    <button
      v-for="r in props.reactions"
      :key="r.emoji"
      type="button"
      class="rxc"
      :class="{ on: r.mine }"
      :aria-pressed="r.mine"
      :aria-label="`${r.emoji} ${r.count}${r.mine ? ', including you' : ''}`"
      data-testid="reaction"
      @click="emit('toggle', r.emoji)"
    >{{ r.emoji }}<span v-if="r.mine" class="rxc__tick" aria-hidden="true">✓</span><b>{{ r.count }}</b></button>
    <button
      ref="trigger"
      type="button"
      class="rxbtn"
      :class="{ on: place }"
      title="React"
      aria-label="React"
      aria-haspopup="menu"
      :aria-expanded="!!place"
      data-testid="react"
      @click="open"
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" /><path d="M8.5 14.5c.9 1.2 2.1 1.8 3.5 1.8s2.6-.6 3.5-1.8" /><path d="M9 9.5h.01M15 9.5h.01" />
      </svg>
      <i aria-hidden="true">+</i>
    </button>
  </div>

  <Teleport to="body">
    <div v-if="place" ref="bar" class="rxbar" data-xl-gen="northstar" role="menu" :style="place" data-testid="reaction-bar">
      <button
        v-for="emoji in QUICK_REACTIONS"
        :key="emoji"
        type="button"
        role="menuitem"
        :aria-label="`React ${emoji}`"
        @click="pick(emoji)"
      >{{ emoji }}</button>
      <span class="sep" aria-hidden="true" />
      <EmojiPickerButton class="more" title="All emoji" aria-label="All emoji" data-testid="reaction-more" @pick="pick">＋</EmojiPickerButton>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.rxrow {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  min-height: 22px;

  &.me { justify-content: flex-end; }
}

.rxc {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 8px;
  border-radius: 99px;
  background: #fff;
  border: 1px solid var(--xl-color-border, #ede7f0);
  font: inherit;
  font-size: 13px;
  line-height: 1;
  color: var(--xl-color-primary-text, #1c1720);
  box-shadow: var(--xl-shadow-1, 0 1px 2px rgba(28, 23, 32, 0.04));
  cursor: pointer;

  b { font-size: 11.5px; font-weight: 600; color: var(--xl-color-outline, #867d8d); }

  &:hover { border-color: #d9c6e8; }

  &.on {
    background: var(--xl-color-primary-container, #f5eff9);
    border-color: var(--xl-color-primary, #5e2f78);
    box-shadow: inset 0 0 0 1px var(--xl-color-primary, #5e2f78);

    b { color: var(--xl-color-primary, #5e2f78); }
  }
}

.rxc__tick {
  margin-right: -2px;
  font-size: 10.5px;
  font-weight: 700;
  color: var(--xl-color-primary, #5e2f78);
}

.rxbtn {
  position: relative;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: none;
  font: inherit;
  color: var(--xl-color-outline-variant, #b6aebc);
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.12s;

  i {
    position: absolute;
    right: 1px;
    bottom: 1px;
    font-size: 10px;
    font-style: normal;
    font-weight: 700;
    line-height: 1;
  }

  &:hover, &.on {
    background: var(--xl-color-primary-container, #f5eff9);
    color: var(--xl-color-primary, #5e2f78);
  }
}

:global(.msgs__line:hover .rxbtn),
.rxrow.has .rxbtn,
.rxbtn.on,
.rxbtn:focus-visible { opacity: 1; }

@media (hover: none) {
  .rxbtn { opacity: 1; }
}

// On the body, so it names the Northstar subtree itself; that subtree otherwise fills the page's height.
.rxbar {
  position: fixed;
  min-height: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 5px 7px;
  background: #fff;
  border: 1px solid #ede7f0;
  border-radius: 99px;
  box-shadow: 0 14px 34px rgba(28, 23, 32, 0.18);
  color: #1c1720;
  letter-spacing: -0.096px;

  button, :deep(button) {
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    font-size: 19px;
    line-height: 1;
    background: none;
    font-family: inherit;
    color: inherit;
    cursor: pointer;

    &:hover { background: #f5eff9; transform: scale(1.12); }
  }

  .sep { width: 1px; height: 20px; background: #ede7f0; margin: 0 3px; }

  :deep(.more) { font-size: 17px; color: #867d8d; }
}
</style>
